import * as Sentry from '@sentry/react';
import { useEffect, useState } from 'react';
import { ZodError } from 'zod';

import { DashboardFilterValues, EMPTY_FILTERS } from '../api/types';
import DayColumn from '../components/DayColumn';
import ShiftDetailPanel from '../components/ShiftDetailPanel';
import ShowFilter from '../components/ShowFilter';
import SummaryPills from '../components/SummaryPills';
import WeekNav from '../components/WeekNav';
import useCalendarWeek from '../hooks/useCalendarWeek';
import useDashboardFilters from '../hooks/useDashboardFilters';
import useWeekNav from '../hooks/useWeekNav';

import LoadingState from '@/components/LoadingState';

// Calendar tab: week nav + Today (left), summary pills + Show filter (right),
// and the 7-column day grid below. Filter state lives here and feeds both the
// Show control and the calendar_week query key.

type Props = { agencyId: number };

const CalendarTab = ({ agencyId }: Props) => {
  const { startDateISO, rangeLabel, goPrev, goNext, goToday } =
    useWeekNav(agencyId);
  const [filters, setFilters] = useState<DashboardFilterValues>(EMPTY_FILTERS);
  const [selectedShiftId, setSelectedShiftId] = useState<number | null>(null);

  const { data, isLoading, isError, error, refetch, isFetching } =
    useCalendarWeek(agencyId, startDateISO, filters);
  const { data: filterOptions } = useDashboardFilters(agencyId);

  // Log the underlying cause so the failure is triageable rather than a silent
  // "Unable to load". A ZodError means the payload drifted from the contract;
  // anything else is an HTTP/request failure.
  useEffect(() => {
    if (!isError || !error) return;
    const kind = error instanceof ZodError ? 'contract' : 'request';
    // eslint-disable-next-line no-console
    console.error(`[DashboardV2] calendar_week ${kind} failure`, error);
    Sentry.captureException(error, {
      tags: { feature: 'dashboard_v2_calendar', failureKind: kind },
      extra: { agencyId, startDate: startDateISO },
    });
  }, [isError, error, agencyId, startDateISO]);

  const totals = data?.totals ?? { critical: 0, at_risk: 0, filled: 0 };

  return (
    <div>
      <div className="dashv2-controls">
        <WeekNav
          rangeLabel={rangeLabel}
          onPrev={goPrev}
          onNext={goNext}
          onToday={goToday}
        />
        <div className="dashv2-summary">
          <SummaryPills totals={totals} />
          <ShowFilter
            options={filterOptions}
            value={filters}
            onChange={setFilters}
          />
        </div>
      </div>

      {isLoading && !data ? (
        <LoadingState />
      ) : isError ? (
        <div className="dashv2-stub">
          <p>Unable to load the calendar.</p>
          <button
            type="button"
            className="dashv2-retry"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            {isFetching ? 'Retrying…' : 'Retry'}
          </button>
        </div>
      ) : (
        <div className="dashv2-calendar">
          {(data?.days ?? []).map((day) => (
            <DayColumn
              key={day.date}
              day={day}
              onSelectShift={setSelectedShiftId}
            />
          ))}
        </div>
      )}

      {selectedShiftId != null && (
        <ShiftDetailPanel
          agencyId={agencyId}
          shiftId={selectedShiftId}
          onClose={() => setSelectedShiftId(null)}
        />
      )}
    </div>
  );
};

export default CalendarTab;
